import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Users, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AcceptInvitation() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');

  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | invalid
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setStatus('invalid'); return; }
    axios.get(`${API}/security/assistants/invite/${token}`)
      .then((res) => { setInvite(res.data); setStatus('ready'); })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const handleAccept = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error('La contraseña debe tener al menos 8 caracteres');
    if (password !== confirm) return toast.error('Las contraseñas no coinciden');
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/security/assistants/accept`, { token, password });
      login(res.data.token, res.data.user);
      toast.success('¡Cuenta de asistente creada!');
      navigate('/dashboard');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Error al aceptar la invitación');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f0720] p-4" data-testid="accept-invitation-page">
      <div className="w-full max-w-md bg-white/5 border border-white/10 rounded-3xl p-8 backdrop-blur-xl">
        <div className="w-14 h-14 mx-auto mb-5 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
          <Users className="w-7 h-7 text-white" />
        </div>

        {status === 'loading' && <p className="text-white/70 text-center">Cargando invitación...</p>}

        {status === 'invalid' && (
          <div className="text-center">
            <h1 className="gotham-bold text-xl text-white mb-2">Invitación no válida</h1>
            <p className="text-white/60 text-sm mb-6">Este enlace expiró o ya fue utilizado.</p>
            <Button onClick={() => navigate('/')} className="btn-viubux w-full">Ir al inicio</Button>
          </div>
        )}

        {status === 'ready' && invite && (
          <div>
            <h1 className="gotham-bold text-xl text-white mb-1 text-center">Únete como Asistente</h1>
            <p className="text-white/60 text-sm text-center mb-1">
              <strong className="text-white">{invite.owner_name}</strong> te invitó a administrar su cuenta.
            </p>
            <p className="text-white/40 text-xs text-center mb-6">{invite.email}</p>

            <div className="bg-black/30 border border-white/10 rounded-xl p-3 mb-5 text-xs text-white/60">
              Podrás gestionar obras, marketing, subidas y mensajes. No tendrás acceso a usuario, correo, datos bancarios ni pagos del autor.
            </div>

            <form onSubmit={handleAccept} className="space-y-4">
              <div className="relative">
                <input
                  data-testid="accept-password-input"
                  type={showPass ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Crea una contraseña"
                  className="w-full px-4 py-3 rounded-xl bg-black/30 border border-purple-500/30 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <button type="button" onClick={() => setShowPass((v) => !v)} className="absolute right-3 top-3.5 text-white/50">
                  {showPass ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <input
                data-testid="accept-confirm-input"
                type={showPass ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirma la contraseña"
                className="w-full px-4 py-3 rounded-xl bg-black/30 border border-purple-500/30 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-violet-500"
              />
              <Button data-testid="accept-invite-submit" type="submit" disabled={submitting} className="btn-viubux w-full">
                {submitting ? 'Creando cuenta...' : 'Aceptar y Crear Cuenta'}
              </Button>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
